import { Aggregator } from 'mingo/aggregator';
import { useOperators, OperatorType, computeValue } from 'mingo/core';
import * as pipelineOperators from 'mingo/operators/pipeline';
import * as expressionOperators from 'mingo/operators/expression';
import * as accumulatorOperators from 'mingo/operators/accumulator';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import snakeCase from 'lodash/snakeCase';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4, validate as uuidValidate, } from 'uuid';
import { postalCodeToTimezone, postalCodeToState } from '@buddy-technology/buddy_helpers';
const hash = require('hash-sum');
function pad(value) {
    return value < 10 ? `0${value}` : value;
}
function createTimezoneOffset(offsetAmount) {
    // const date = _date || new Date();
    const sign = (offsetAmount > 0) ? '-' : '+';
    const offset = Math.abs(offsetAmount);
    const hours = pad(Math.floor(offset / 60));
    const minutes = pad(offset % 60);
    return `${sign + hours}:${minutes}`;
}
function getTimezoneOffset(dt, timezone) {
    const getItem = (format) => {
        // eslint-disable-next-line no-param-reassign
        format.timeZone = timezone;
        // eslint-disable-next-line radix
        return parseInt(dt.toLocaleString('en-US', format));
    };
    const adjDate = new Date(getItem({ year: 'numeric' }), getItem({ month: 'numeric' }) - 1, // months are zero based
    getItem({ day: 'numeric' }), getItem({ hour: 'numeric', hour12: false }), getItem({ minute: 'numeric' }));
    const noSecs = new Date(dt.getTime());
    noSecs.setSeconds(0, 0);
    const diff = Math.round((adjDate.getTime() - noSecs.getTime()) / 60000);
    return dt.getTimezoneOffset() - diff;
}
useOperators(OperatorType.PIPELINE, pipelineOperators);
useOperators(OperatorType.ACCUMULATOR, accumulatorOperators);
useOperators(OperatorType.EXPRESSION, expressionOperators);
// adding customer operators
useOperators(OperatorType.EXPRESSION, {
    $numberToCurrency(collection, expr, options) {
        const defaults = {
            locale: 'en-US',
            currency: 'USD',
            minimumFractionDigits: 2,
        };
        const value = computeValue(collection, expr.value, null, options);
        // value is required
        if (!value) {
            throw new Error('Must provide "value" property.');
        }
        if (typeof value !== 'number') {
            throw new Error('The property "value" must be a number.');
        }
        const locale = computeValue(collection, expr.locale, null, options) || defaults.locale;
        // locale must be string type
        if (typeof locale !== 'string') {
            throw new Error('The property "locale", if provided, must be a string.');
        }
        const currency = computeValue(collection, expr.currency, null, options) || defaults.currency;
        if (typeof currency !== 'string') {
            throw new Error('The property "currency", if provided, must be a string.');
        }
        const minimumFractionDigits = computeValue(collection, expr.minimumFractionDigits, null, options)
            ?? defaults.minimumFractionDigits;
        if (typeof minimumFractionDigits !== 'number') {
            throw new Error('The property "minimumFractionDigits", if provided, must be a number 0 or higher.');
        }
        return Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits,
        }).format(value);
    },
    $roundTo(collection, expr, options) {
        const value = computeValue(collection, expr?.[0], null, options);
        // value is required
        if (!value) {
            throw new Error('Must provide first element.');
        }
        if (typeof value !== 'number') {
            throw new Error('First element must be a number.');
        }
        const digits = computeValue(collection, expr?.[1], null, options) ?? 0;
        if (typeof digits !== 'number') {
            throw new Error('Second element must be a number when provided.');
        }
        const multiplicator = 10 ** digits;
        const newN = parseFloat((value * multiplicator).toFixed(11));
        const test = Math.round(newN) / multiplicator;
        return +test.toFixed(digits);
    },
    $roundToString(collection, expr, options) {
        const value = computeValue(collection, expr?.[0], null, options);
        // value is required
        if (!value) {
            throw new Error('Must provide first element.');
        }
        if (typeof value !== 'number') {
            throw new Error('First element must be a number.');
        }
        const digits = computeValue(collection, expr?.[1], null, options) ?? 0;
        if (typeof digits !== 'number') {
            throw new Error('Second element must be a number when provided.');
        }
        const multiplicator = 10 ** digits;
        const newN = parseFloat((value * multiplicator).toFixed(11));
        const test = Math.round(newN) / multiplicator;
        return Intl.NumberFormat('en-US', {
            minimumFractionDigits: digits,
            useGrouping: false,
        }).format(+test.toFixed(digits));
    },
    $elementFromIndex(collection, expr, options) {
        const array = computeValue(collection, expr.array, null, options);
        if (!array) {
            throw new Error('"array" is a required property.');
        }
        else if (!Array.isArray(array)) {
            throw new Error('The "array" property must be an array.');
        }
        else if (!array.length) {
            throw new Error('The "array" property must have at least 1 element in it.');
        }
        const index = computeValue(collection, expr.index, null, options);
        if (!index && index !== 0) {
            throw new Error('"index" is a required property.');
        }
        else if (typeof index !== 'number') {
            throw new Error('The "index" property must be a number.');
        }
        // if it's a negative index, go to the end
        if (index < 0) {
            // we subtract 1 automatically, because the length is 1 more
            // than the last index
            return array[array.length + index];
        }
        return array[index];
    },
    $objectPropertyFromPath(collection, expr, options) {
        const object = computeValue(collection, expr.object, null, options);
        if (!object) {
            throw new Error('"object" is a required property.');
        }
        else if (typeof object !== 'object') {
            throw new Error('The "object" property must be an object.');
        }
        else if (!Object.keys(object).length) {
            throw new Error('The "object" property cannot be empty.');
        }
        const path = computeValue(collection, expr.path, null, options);
        if (!path) {
            throw new Error('"path" is a required property.');
        }
        else if (typeof path !== 'string') {
            throw new Error('The "path" property must be a string.');
        }
        // we cant use "default" as a property name, so named it with an underscore
        // eslint-disable-next-line no-unused-vars
        const _default = computeValue(collection, expr.default, null, options);
        return _default === undefined ? get(object, path) : get(object, path, _default);
    },
    $postalCodeToTimezone(collection, expr, options) {
        const postalCode = computeValue(collection, expr, null, options);
        if (!postalCode || typeof postalCode !== 'string') {
            throw new Error('The postalCode is a required string property.');
        }
        return postalCodeToTimezone(postalCode);
    },
    $postalCodeToUtcOffset(collection, expr, options) {
        const postalCode = computeValue(collection, expr, null, options);
        if (!postalCode || typeof postalCode !== 'string') {
            throw new Error('The postalCode is a required string property.');
        }
        const timeZone = postalCodeToTimezone(postalCode);
        const offset = getTimezoneOffset(new Date(), timeZone);
        return createTimezoneOffset(offset);
    },
    $postalCodeToState(collection, expr, options) {
        const postalCode = computeValue(collection, expr, null, options);
        if (!postalCode || typeof postalCode !== 'string') {
            throw new Error('The postalCode is a required string property.');
        }
        return postalCodeToState(postalCode);
    },
    $kebabCase(collection, expr, options) {
        const str = computeValue(collection, expr, null, options);
        if (!str || typeof str !== 'string') {
            throw new Error('Must provide a string.');
        }
        return kebabCase(str);
    },
    $snakeCase(collection, expr, options) {
        const str = computeValue(collection, expr, null, options);
        if (!str || typeof str !== 'string') {
            throw new Error('Must provide a string.');
        }
        return snakeCase(str);
    },
    $camelCase(collection, expr, options) {
        const str = computeValue(collection, expr, null, options);
        if (!str || typeof str !== 'string') {
            throw new Error('Must provide a string.');
        }
        return camelCase(str);
    },
    $startCase(collection, expr, options) {
        const str = computeValue(collection, expr, null, options);
        if (!str || typeof str !== 'string') {
            throw new Error('Must provide a string.');
        }
        return startCase(str);
    },
    $capitalize(collection, expr, options) {
        const str = computeValue(collection, expr, null, options);
        if (!str || typeof str !== 'string') {
            throw new Error('Must provide a string.');
        }
        return capitalize(str);
    },
    $hash(collection, expr, options) {
        const val = computeValue(collection, expr, null, options);
        return hash(val);
    },
    // uuid stuff
    $uuid(collection, expr, options) {
        const optionsPassed = computeValue(collection, expr, null, options);
        return uuidv4(optionsPassed || {});
    },
    $validateUUID(collection, expr, options) {
        const val = computeValue(collection, expr, null, options);
        if (typeof val !== 'string') {
            throw new Error('Value of operator must be of type string.');
        }
        return uuidValidate(val);
    },
});
const runMingo = (script, data, showRawResult = false) => {
    // we clone to now mutate the data coming in
    const clone = cloneDeep(data);
    try {
        const calculator = new Aggregator(script);
        const info = Array.isArray(clone) ? clone : [clone];
        const rawResult = calculator.run(info);
        // incase someone wants to see ALL the things
        if (showRawResult) {
            return {
                value: rawResult,
                error: null,
            };
        }
        const [result] = rawResult;
        const value = 'value' in result ? result.value : result;
        return {
            value,
            error: null,
        };
    }
    catch (error) {
        return {
            value: null,
            error,
            // had to cast here since ts-linter says catch block arg MUST be of type 'any' or 'unknown' and thus can't be Error.
        };
    }
};
export default runMingo;
